import {CSSProperties} from 'react';
import * as PropTypes from 'prop-types';
import {DateFilterItem, LayerPrintMode, SymbolicDate} from '../enums';
import {MapEvent} from '../events';
import {
    Coordinate,
    CssPadding,
    DateFilterItemDefinition,
    FeatureAction,
    FeatureFilter,
    LayerId,
    OptValidator,
    Params,
} from '../types';
import {getMapioServerUrl} from '../utils';
import {Layer} from '../types/Layer';

/**
 * Properties společné pro všechny mapové komponenty.
 *
 * @typeparam Event - Typ události kterou mapová komponenta generuje.
 */
export interface MapProps<Event extends MapEvent> {
    /**
     * Pomocné jméno pro ladíčí účely.
     * Používá se ve výpisech ladících zpráv do konzole.
     */
    debugName?: string;

    /**
     * Class jméno komponenty.
     */
    className?: string;

    /**
     * CSS styl komponenty.
     */
    style?: CSSProperties;

    /**
     * URL mapového serveru.
     * Výchozí hodnota: `window.MAPIO_URL` nebo `window.appConfig.dependUrls.mapio`.
     */
    serverUrl?: string;

    /**
     * Autentizační údaje.
     *
     * Aktuálně se předává napřímo login (plain text nebo obfuskovaný pomocí
     * server funkce `CipherTools.encodeIfDecoded`), bohužel :-(.
     *
     * V budoucnu bude doufejme předěláno na autentizační token.
     */
    authentication?: string;

    /**
     * Query parametry, které budou přidány do URL mapového serveru.
     */
    queryParams?: Params;

    /**
     * Kontextové parametry, určené pro aplikační konektor na straně mapového serveru.
     */
    contextParams?: Params;

    /**
     * Odsazení ovládacích prvků mapy od okrajů stránky.
     */
    padding?: CssPadding;

    /**
     * Dostupné položky datumového filtru.
     * Lze použít buď hodnoty z enumu {@link DateFilterItem}, nebo vlastní definice {@link DateFilterItemDefinition}.
     */
    dateFilterItems?: (DateFilterItem | DateFilterItemDefinition)[];

    /**
     * Akce, které lze provést nad features.
     * Jsou viditelné v detailu feature.
     */
    featureActions?: FeatureAction[];

    /**
     * Povolení zobrazení tooltipů nad features.
     * Výchozí hodnota: `true` pro editor, `false` pro zobrazovač.
     */
    featureTooltips?: boolean;

    /**
     * Příznak, zda je povoleno vkládání externích vrstev uživatelem.
     * Výchozí hodnota: `false`
     */
    externalLayersEnabled?: boolean;

    /**
     * Příznak, zda je povoleno nastavování parametrů vrstev uživatelem.
     * Výchozí hodnota: `false`
     */
    layerSettingsEnabled?: boolean;

    /**
     * Příznak, zda zobrazovat katalog menu.
     */
    catalogMenuVisible?: boolean;

    /**
     * Režim tisku rastrových vrstev.
     * Výchozí hodnota: {@link LayerPrintMode.TOPIC}.
     */
    rasterLayersPrintMode?: LayerPrintMode;

    /**
     * Režim tisku vektorových vrstev.
     * Výchozí hodnota: {@link LayerPrintMode.TOPIC}.
     */
    vectorLayersPrintMode?: LayerPrintMode;

    /**
     * Příznak, zda zobrazovat zaměřovač ve středu mapy.
     * Výchozí hodnota: `false`.
     */
    reticleVisible?: boolean;

    /**
     * Výchozí téma konfigurace.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    initialTopic?: string;

    /**
     * Výchozí ID podkladové vrstvy.
     *
     * `null` znamená nezobrazuj žádnou podkladovou vrstvu.
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    initialBackgroundLayer?: number | null;

    /**
     * Výchozí ID popisné vrstvy.
     *
     * `null` znamená nezobrazuj žádnou popisnou vrstvu.
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    initialForegroundLayer?: number | null;

    /**
     * Výchozí ID viditelných datových vrstev.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    initialLayers?: number[];

    /**
     * Výchozí ID rozbalených položek katalogu.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    initialCatalogNodes?: string[];

    /**
     * Výchozí pozice středu mapy.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    initialLocation?: Coordinate;

    /**
     * Výchozí úroveň přiblížení mapy.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    initialZoom?: number;

    /**
     * Výchozí filtr obsahu datových vrstev.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    initialFilter?: FeatureFilter;

    /**
     * Příznak, zda je povoleno uživatelsky přepínat téma.
     * Výchozí hodnota: `true`
     */
    topicChangeEnabled?: boolean;

    /**
     * Callback volaný v okamžiku dokončení inicializace mapové stránky ve frame.
     *
     * V tento okamžik je již možno s mapovou stránkou bezpečně komunikovat
     * s použitím dispatche akcí (např {@link EditorActions.selectFeatures}).
     */
    onReady?: () => void;

    /**
     * Callback volaný při vzniku události mapové komponenty.
     *
     * @param event - Událost
     */
    onEvent?: (event: Event) => void;

    /**
     * Callback volaný při změně téma konfigurace.
     *
     * @param topic - Nová hodnota parametru.
     */
    onTopicChange?: (topic: string) => void;

    /**
     * Callback volaný při změně ID podkladové vrstvy.
     *
     * @param backgroundLayer - Nová hodnota parametru.
     */
    onBackgroundLayerChange?: (backgroundLayer: LayerId) => void;

    /**
     * Callback volaný při změně ID popisné vrstvy.
     *
     * @param foregroundLayer - Nová hodnota parametru.
     */
    onForegroundLayerChange?: (foregroundLayer: LayerId) => void;
    /**
     * Callback volaný při změně parametru ID viditelných datových vrstev.
     *
     * @param layers - Nová hodnota parametru.
     */
    onLayersChange?: (layers: LayerId[]) => void;

    /**
     * Callback volaný při změně dostupných datových vrstev (např. při jejich prvotním načtení či po změně topic).
     *
     * @param layers - Nový seznam dostupných datových vrstev.
     */
    onAllLayersChange?: (layers: Layer[]) => void;

    /**
     * Callback volaný při změně parametru ID zapisovatelných datových vrstev.
     *
     * @param layers - Nová hodnota parametru.
     */

    onWritableLayersChange?: (layers: LayerId[]) => void;

    /**
     * Callback volaný při změně ID rozbalených položek katalogu.
     *
     * @param catalogNodes - Nová hodnota parametru.
     */
    onCatalogNodesChange?: (catalogNodes: string[]) => void;

    /**
     * Callback volaný při změně pozice středu mapy.
     *
     * @param location - Nová hodnota parametru.
     */
    onLocationChange?: (location: Coordinate) => void;

    /**
     * Callback volaný při změně úrovně přiblížení mapy.
     *
     * @param zoom - Nová hodnota parametru.
     */
    onZoomChange?: (zoom: number) => void;

    /**
     * Callback volaný při změně filtru obsahu datových vrstev.
     *
     * @param filter - Nová hodnota parametru.
     */
    onFilterChange?: (filter: FeatureFilter) => void;
}

/**
 * Typy základních properties všech mapových komponent.
 *
 * @ignore
 */
export const mapPropTypes = {
    debugName: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    serverUrl: PropTypes.string,
    authentication: PropTypes.string,
    queryParams: PropTypes.objectOf(PropTypes.any),
    contextParams: PropTypes.objectOf(PropTypes.any),
    padding: PropTypes.arrayOf(PropTypes.string.isRequired) as OptValidator<CssPadding>,
    dateFilterItems: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.oneOf(Object.values(DateFilterItem)),
            PropTypes.exact({
                id: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                value: PropTypes.oneOfType([
                    PropTypes.exact({
                        dateFrom: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
                        dateTo: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
                    }),
                    PropTypes.oneOf([null]),
                ]).isRequired,
            }),
        ]).isRequired,
    ) as OptValidator<(DateFilterItem | DateFilterItemDefinition)[]>,
    featureActions: PropTypes.arrayOf(
        PropTypes.exact({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        }).isRequired,
    ),
    featureTooltips: PropTypes.bool,
    externalLayersEnabled: PropTypes.bool,
    layerSettingsEnabled: PropTypes.bool,
    catalogMenuVisible: PropTypes.bool,
    rasterLayersPrintMode: PropTypes.oneOf(Object.values(LayerPrintMode)),
    vectorLayersPrintMode: PropTypes.oneOf(Object.values(LayerPrintMode)),
    reticleVisible: PropTypes.bool,
    initialTopic: PropTypes.string,
    initialBackgroundLayer: PropTypes.number,
    initialForegroundLayer: PropTypes.number,
    initialLayers: PropTypes.arrayOf(PropTypes.number.isRequired),
    initialCatalogNodes: PropTypes.arrayOf(PropTypes.string.isRequired),
    initialLocation: PropTypes.arrayOf(PropTypes.number.isRequired) as OptValidator<Coordinate>,
    initialZoom: PropTypes.number,
    initialFilter: PropTypes.exact({
        date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf(Object.values(SymbolicDate))]),
        dateFrom: PropTypes.instanceOf(Date),
        dateTo: PropTypes.instanceOf(Date),
    }),
    topicChangeEnabled: PropTypes.bool,
    onReady: PropTypes.func,
    onEvent: PropTypes.func,
    onTopicChange: PropTypes.func,
    onBackgroundLayerChange: PropTypes.func,
    onForegroundLayerChange: PropTypes.func,
    onLayersChange: PropTypes.func,
    onAllLayersChange: PropTypes.func,
    onWritableLayersChange: PropTypes.func,
    onCatalogNodesChange: PropTypes.func,
    onLocationChange: PropTypes.func,
    onZoomChange: PropTypes.func,
    onFilterChange: PropTypes.func,
};

/**
 * Výchozí hodnoty základních properties všech mapových komponent.
 *
 * @ignore
 */
export const mapDefaultProps = {
    // Zcela záměrně neuvádíme default hodnoty properties, které se synchronizují s mapio.
    // 1) Jsou definovány na straně mapio. Neduplikujeme tím pádem logiku
    //    a neposíláme zbytečné zprávy přes frame.
    // 2) V rámci zpětné kompatibility udržujeme některé deprecated properties.
    //    Default ostatních properties by je mohl přepsat (pokud by šlo o alias).
    serverUrl: getMapioServerUrl(),
};
